import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAddress } from '../contexts/AddressContext';
import { useCart } from '../contexts/CartContext';
import { MapPin, ChevronRight, Loader2 } from 'lucide-react';
import LocationPicker from '../components/LocationPicker';
import { ListSection } from '../components/ios';

const PARISHES = [
  'Saint George Basseterre',
  'Saint Thomas Middle Island',
  'Saint Mary Cayon',
  'Saint John Capisterre',
  'Saint Paul Capisterre',
  'Saint Peter Basseterre',
  'Saint Anne Sandy Point',
  'Christ Church Nichola Town',
  'Saint George Gingerland',
  'Saint James Windward',
  'Saint John Figtree',
  'Saint Thomas Lowland'
];

const ISLANDS = ['Saint Kitts', 'Nevis'];

// iOS form TextField row — placeholder-only input inside a grouped list row.
function FieldRow({ value, onChange, placeholder, type = 'text' }) {
  return (
    <div className="px-4 py-1 min-h-[44px] flex items-center">
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full py-2 bg-transparent text-[16px] text-gray-900 outline-none placeholder:text-[#C7C7CC]"
      />
    </div>
  );
}

// iOS navigationLink-style Picker row — label left, value + chevron right.
function PickerRow({ label, value, onChange, options }) {
  return (
    <label className="flex items-center justify-between gap-3 px-4 py-2.5 min-h-[44px] cursor-pointer">
      <span className="text-[16px] text-gray-900 shrink-0">{label}</span>
      <span className="flex items-center gap-1 min-w-0">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none bg-transparent text-[16px] text-[#8E8E93] text-right outline-none cursor-pointer max-w-[220px] truncate"
        >
          {options.map((opt) => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
        <ChevronRight size={15} className="text-[#C7C7CC] shrink-0" />
      </span>
    </label>
  );
}

// iOS-style toggle switch (mirrors SwiftUI Toggle)
function IosSwitch({ checked, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`w-[51px] h-[31px] rounded-full p-[2px] transition-colors shrink-0 ${
        checked ? 'bg-[#34C759]' : 'bg-[#E9E9EB]'
      }`}
    >
      <span
        className={`block w-[27px] h-[27px] rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-[20px]' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function AddressFormView() {
  const navigate = useNavigate();
  const location = useLocation();
  const { addressId } = useParams();
  const { addresses, addAddress, updateAddress } = useAddress();
  const { updateDeliveryAddress } = useCart();

  // iOS dismisses the address sheet back into checkout — when the form was
  // opened from there, return to /checkout and select the saved address.
  const returnTo = location.state?.from === 'checkout' ? '/checkout' : '/profile';

  // Form state
  const [name, setName] = useState('');
  const [street, setStreet] = useState('');
  const [apartment, setApartment] = useState('');
  const [landmark, setLandmark] = useState('');
  const [parish, setParish] = useState('Saint George Basseterre');
  const [island, setIsland] = useState('Saint Kitts');
  const [instructions, setInstructions] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [showMap, setShowMap] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Load existing address data if editing
  useEffect(() => {
    if (addressId && addresses.length > 0) {
      const address = addresses.find(addr => addr.id === addressId);
      if (address) {
        console.log('Loading existing address:', address);
        setName(address.name || '');
        setStreet(address.street || '');
        setApartment(address.apartment || '');
        setLandmark(address.landmark || '');
        setParish(address.parish || 'Saint George Basseterre');
        setIsland(address.island || 'Saint Kitts');
        setInstructions(address.instructions || '');
        setIsDefault(address.isDefault || false);
        if (address.coordinates) {
          setCoordinates({
            latitude: address.coordinates.latitude || 0,
            longitude: address.coordinates.longitude || 0
          });
        }
      }
    }
  }, [addressId, addresses]);

  // Validation (mirrors iOS AddressFormViewModel.isValid)
  const isValid = name.trim() && street.trim() && coordinates;

  const handleSave = async () => {
    if (!isValid) return;

    setLoading(true);
    setError('');

    try {
      const addressData = {
        name: name.trim(),
        street: street.trim(),
        apartment: apartment.trim() || null,
        landmark: landmark.trim() || null,
        parish,
        island,
        instructions: instructions.trim() || null,
        coordinates: {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        },
        isDefault
      };

      let savedId = addressId;
      if (addressId) {
        // Update existing address
        await updateAddress(addressId, addressData);
        console.log('Address updated successfully');
      } else {
        // Add new address
        savedId = await addAddress(addressData);
        console.log('Address added successfully');
      }

      // Back to checkout with the saved address selected — iOS sheet flow.
      if (returnTo === '/checkout') {
        updateDeliveryAddress({ id: savedId, ...addressData });
      }
      navigate(returnTo);
    } catch (err) {
      setError(err.message || 'Failed to save address');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (locationData) => {
    console.log('📍 Location selected:', locationData);
    const { coordinates, street, parish, island } = locationData;

    setCoordinates({
      latitude: coordinates.latitude,
      longitude: coordinates.longitude
    });
    setStreet(street || '');
    setParish(parish || '');
    setIsland(island || 'Saint Kitts');
    setShowMap(false);
  };

  if (showMap) {
    return (
      <LocationPicker
        onLocationSelect={handleLocationSelect}
        onClose={() => setShowMap(false)}
        initialCoordinates={coordinates ? { lat: coordinates.latitude, lng: coordinates.longitude } : null}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2F7]">
      {/* iOS inline navigation bar: Cancel / Title / Save */}
      <header className="sticky top-0 z-40 bg-white shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="relative flex items-center justify-between px-4 h-12 max-w-lg mx-auto">
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            className="text-[16px] text-[#FF3B30] active:opacity-60"
          >
            Cancel
          </button>
          <h1 className="absolute left-1/2 -translate-x-1/2 text-[16px] font-semibold text-gray-900">
            {addressId ? 'Edit Address' : 'Add Address'}
          </h1>
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid || loading}
            className="text-[16px] font-semibold text-[#FF3B30] active:opacity-60 disabled:opacity-40 flex items-center gap-1.5"
          >
            {loading && <Loader2 size={14} className="animate-spin" />}
            Save
          </button>
        </div>
      </header>

      <div className="max-w-lg mx-auto pb-8">
        {/* Error banner */}
        {error && (
          <div className="px-4 mt-4">
            <div className="bg-white rounded-xl px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
              <p className="text-[15px] text-[#FF3B30]">{error}</p>
            </div>
          </div>
        )}

        {/* Address Details */}
        <ListSection title="Address Details">
          <FieldRow
            value={name}
            onChange={setName}
            placeholder="Name (e.g., Home, Work)"
          />
          <FieldRow
            value={street}
            onChange={setStreet}
            placeholder="Street Address"
          />
          <FieldRow
            value={apartment}
            onChange={setApartment}
            placeholder="Apartment/Suite/Unit (Optional)"
          />
          <FieldRow
            value={landmark}
            onChange={setLandmark}
            placeholder="Landmark (Optional)"
          />
        </ListSection>

        {/* Location */}
        <ListSection title="Location">
          <PickerRow
            label="Parish"
            value={parish}
            onChange={setParish}
            options={PARISHES}
          />
          <PickerRow
            label="Island"
            value={island}
            onChange={setIsland}
            options={ISLANDS}
          />
          <button
            type="button"
            onClick={() => setShowMap(true)}
            className="w-full flex items-center gap-3 px-4 py-3 min-h-[44px] text-left active:bg-gray-100 transition-colors"
          >
            <MapPin size={20} className="text-[#FF3B30] shrink-0" />
            <span className="flex-1 text-[16px] text-gray-900">Set Location Pin</span>
            {coordinates && (
              <span className="flex items-center gap-1 text-[13px] text-[#6D6D72] shrink-0">
                <MapPin size={13} />
                Set
              </span>
            )}
          </button>
        </ListSection>

        {/* Delivery Instructions */}
        <ListSection title="Delivery Instructions">
          <FieldRow
            value={instructions}
            onChange={setInstructions}
            placeholder="Special instructions for delivery"
          />
        </ListSection>

        {/* Default toggle */}
        <ListSection>
          <div className="flex items-center justify-between gap-3 px-4 py-3 min-h-[44px]">
            <span className="text-[16px] text-gray-900">Set as Default Address</span>
            <IosSwitch checked={isDefault} onChange={setIsDefault} />
          </div>
        </ListSection>

        {/* Save button — mirrors iOS AddAddressView "Save Location" section button */}
        <div className="px-4 mt-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={!isValid || loading}
            className="w-full py-3 rounded-xl bg-[#FF3B30] text-white text-[17px] font-semibold active:opacity-80 disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {loading ? 'Saving…' : 'Save Address'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddressFormView;
